/**
 * Drop-in replacement for `react-toastify`'s `toast` API that funnels every
 * call through our AlertProvider via alertBus. Same import shape as before:
 *
 *   import { toast } from "<this file>";
 *   toast.success("Saved");
 *   toast.error("Login failed");
 *
 * Each `toast.<level>(message, options?)` call becomes an alert with:
 *   title   → capitalized level ("Success", "Error", …)
 *   message → the original string
 *   duration → options.autoClose if a number, else default
 *
 * If you also call `toast(...)` (the function form), we treat it as info.
 */

import { alertBus } from "./alertBus";

interface ToastOptions {
  autoClose?: number | false;
  position?: string;
  // any other react-toastify options are ignored
  [key: string]: any;
}

const TITLE_BY_LEVEL = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
} as const;

function durationFrom(opts?: ToastOptions): number | undefined {
  if (!opts) return undefined;
  if (opts.autoClose === false) return 0; // sticky
  if (typeof opts.autoClose === "number") return opts.autoClose;
  return undefined;
}

function call(
  level: "success" | "error" | "warning" | "info",
  message: string,
  opts?: ToastOptions
) {
  alertBus.show({
    type: level,
    title: TITLE_BY_LEVEL[level],
    message,
    duration: durationFrom(opts),
  });
}

interface ToastApi {
  (message: string, opts?: ToastOptions): void;
  success: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  warn: (message: string, opts?: ToastOptions) => void;
  warning: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
  dismiss: () => void;
}

const fn = ((message: string, opts?: ToastOptions) => call("info", message, opts)) as ToastApi;
fn.success = (message, opts) => call("success", message, opts);
fn.error = (message, opts) => call("error", message, opts);
fn.warn = (message, opts) => call("warning", message, opts);
fn.warning = (message, opts) => call("warning", message, opts);
fn.info = (message, opts) => call("info", message, opts);
fn.dismiss = () => alertBus.hide();

export const toast = fn;
export default fn;
